/**
 * `<caos-artifact-list>` — the code artifacts in an environment, each row naming itself twice.
 * (Catalog: Developer surfaces › ArtifactList — pick a code artifact from a long list, each row carrying its real name and what kind of thing it is.)
 *
 * TWO LINES PER ROW, and that is the whole design. The real name goes on the first line and what
 * kind of thing it is plus its human label goes underneath in smaller type. With several hundred
 * artifacts that second line is the difference between a usable picker and an alphabetical wall —
 * `AccountTrigger` and `AccountTriggerHandler` are indistinguishable at a glance, and the type is
 * what tells them apart.
 *
 * This is a gap the specification names explicitly: the platform's own option is a single string
 * with nowhere to put the qualifier. So this component is a LIST rather than a `<select>` — a row is
 * a real button that can hold two lines, be styled, and carry its own accessible name reading both.
 *
 * DIRTY IS SHOWN ON THE ROW, not only on the editor tab. Somebody who has edited four artifacts and
 * gone back to the list needs to see which four from here; a mark that only exists on the tab strip
 * is a mark you have to already be looking at the right thing to see.
 *
 * FILTERING IS BY TYPE AND BY TEXT, and the two intersect. The type filter's contents are the types
 * PRESENT, because offering a type this environment has none of is offering an empty result.
 *
 * Styling seams: `::part(list)`, `::part(filters)`, `::part(type)`, `::part(search)`,
 * `::part(summary)`, `::part(rows)`, `::part(row)`, `::part(name)`, `::part(qualifier)`,
 * `::part(dirty)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** One code artifact. */
export interface CaosArtifact {
  /** The real name — the identity, and what events key on. */
  readonly name: string;
  /** What kind of thing it is, as a person reads it: `Apex Class`, `Apex Trigger`, `Page`. */
  readonly type: string;
  /** The stored type name, when it differs from the friendly one — `ApexClass`, `ApexTrigger`. */
  readonly storedType?: string;
  /** A human label, when the artifact has one that is not just its name again. */
  readonly label?: string;
  /** This artifact has unsaved changes open somewhere. */
  readonly dirty?: boolean;
}

const sheet = makeSheet(`
  :host { display: block; }

  .list { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .filters { display: flex; gap: var(--caos-space-2); flex-wrap: wrap; }
  .filters select, .filters input {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 9px;
  }
  .filters input { flex: 1 1 180px; min-width: 140px; }
  .filters select:focus-visible, .filters input:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .summary {
    font-family: var(--caos-font);
    font-size: 12.5px;
    color: var(--caos-color-text-muted);
  }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }

  .rows {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    overflow: hidden;
  }

  .row {
    display: flex;
    align-items: baseline;
    gap: var(--caos-space-2);
    border: 0;
    border-bottom: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface);
    text-align: start;
    padding: 7px 10px;
    cursor: pointer;
    width: 100%;
  }
  .row:last-child { border-bottom: 0; }
  .row:hover { background: var(--caos-color-surface-2); }
  .row[aria-current="true"] { background: var(--caos-color-accent-soft); }
  .row:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .stack { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1 1 auto; }

  .name {
    font-family: var(--caos-font-mono);
    font-size: 12.5px;
    color: var(--caos-color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* The second line. Smaller and quieter, because it qualifies the name rather than competing
     with it — but always present, which is the point of the component. */
  .qualifier {
    font-family: var(--caos-font);
    font-size: 11.5px;
    color: var(--caos-color-text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dirty { color: var(--caos-color-accent); font-weight: 700; flex: 0 0 auto; }

  .empty {
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text-muted);
  }
`);

/** How one artifact's second line reads. Exported so a surface can say the same thing elsewhere. */
export function artifactQualifier(artifact: CaosArtifact): string {
  const stored = artifact.storedType && artifact.storedType !== artifact.type ? ` (${artifact.storedType})` : '';
  const label = artifact.label && artifact.label !== artifact.name ? ` · ${artifact.label}` : '';
  return `${artifact.type}${stored}${label}`;
}

/** The distinct types present, in the order they first occur — the contents of the type filter. */
export function artifactTypesOf(artifacts: readonly CaosArtifact[]): readonly string[] {
  const seen: string[] = [];
  for (const a of artifacts) if (!seen.includes(a.type)) seen.push(a.type);
  return seen;
}

export class CaosArtifactList extends CaosElement {
  static get observedAttributes(): string[] {
    return ['type', 'search', 'selected', 'empty-text'];
  }

  private typeEl: HTMLSelectElement;
  private searchEl: HTMLInputElement;
  private summaryEl: HTMLParagraphElement;
  private rowsEl: HTMLDivElement;

  private data: readonly CaosArtifact[] = [];

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'list';
    wrap.setAttribute('part', 'list');

    const filters = document.createElement('div');
    filters.className = 'filters';
    filters.setAttribute('part', 'filters');

    this.typeEl = document.createElement('select');
    this.typeEl.setAttribute('part', 'type');
    this.typeEl.setAttribute('aria-label', 'Artifact type');
    this.typeEl.addEventListener('change', () => {
      if (this.typeEl.value === '') this.removeAttribute('type');
      else this.setAttribute('type', this.typeEl.value);
    });

    this.searchEl = document.createElement('input');
    this.searchEl.type = 'search';
    this.searchEl.setAttribute('part', 'search');
    this.searchEl.placeholder = 'Search artifacts';
    this.searchEl.setAttribute('aria-label', 'Search artifacts by name or label');
    this.searchEl.addEventListener('input', () => this.setAttribute('search', this.searchEl.value));

    filters.append(this.typeEl, this.searchEl);

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.rowsEl = document.createElement('div');
    this.rowsEl.className = 'rows';
    this.rowsEl.setAttribute('part', 'rows');
    this.rowsEl.setAttribute('role', 'list');

    wrap.append(filters, this.summaryEl, this.rowsEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(name: string, _before: string | null, after: string | null): void {
    if (name === 'search' && this.searchEl.value !== (after ?? '')) this.searchEl.value = after ?? '';
    this.render();
  }

  get artifacts(): readonly CaosArtifact[] {
    return this.data;
  }

  set artifacts(next: readonly CaosArtifact[]) {
    this.data = Array.isArray(next) ? next : [];
    this.render();
  }

  /** The artifacts surviving both filters, in the order they arrived. */
  get visibleArtifacts(): readonly CaosArtifact[] {
    const type = this.getAttribute('type');
    const term = (this.getAttribute('search') ?? '').trim().toLowerCase();
    return this.data.filter((a) => {
      if (type !== null && type !== '' && a.type !== type) return false;
      if (term === '') return true;
      return (
        a.name.toLowerCase().includes(term) ||
        (a.label ?? '').toLowerCase().includes(term) ||
        a.type.toLowerCase().includes(term)
      );
    });
  }

  /** The artifacts with unsaved changes open somewhere. */
  get dirtyArtifacts(): readonly CaosArtifact[] {
    return this.data.filter((a) => a.dirty === true);
  }

  private choose(artifact: CaosArtifact): void {
    this.setAttribute('selected', artifact.name);
    this.dispatchEvent(
      new CustomEvent('caos-artifact-open', { bubbles: true, composed: true, detail: { artifact } }),
    );
  }

  private render(): void {
    this.renderTypes();

    const visible = this.visibleArtifacts;
    const nodes: Node[] = [];
    const strong = document.createElement('strong');
    strong.textContent =
      visible.length === this.data.length
        ? `${this.data.length} ${this.data.length === 1 ? 'artifact' : 'artifacts'}`
        : `${visible.length} of ${this.data.length} artifacts`;
    nodes.push(strong);
    const dirty = this.dirtyArtifacts.length;
    // Counted over the WHOLE set, not the filtered one: unsaved work that a filter has hidden is
    // exactly the unsaved work somebody is about to forget about.
    if (dirty > 0) nodes.push(document.createTextNode(` · ${dirty} with unsaved changes`));
    this.summaryEl.replaceChildren(...nodes);

    if (visible.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'empty';
      empty.textContent = this.getAttribute('empty-text') ?? 'No artifacts match.';
      this.rowsEl.replaceChildren(empty);
      return;
    }

    const selected = this.getAttribute('selected');
    this.rowsEl.replaceChildren(
      ...visible.map((a) => {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'row';
        row.setAttribute('part', 'row');
        row.setAttribute('role', 'listitem');
        if (a.name === selected) row.setAttribute('aria-current', 'true');

        const stack = document.createElement('span');
        stack.className = 'stack';
        const name = document.createElement('span');
        name.className = 'name';
        name.setAttribute('part', 'name');
        name.textContent = a.name;
        const qualifier = document.createElement('span');
        qualifier.className = 'qualifier';
        qualifier.setAttribute('part', 'qualifier');
        qualifier.textContent = artifactQualifier(a);
        stack.append(name, qualifier);
        row.append(stack);

        if (a.dirty) {
          const dot = document.createElement('span');
          dot.className = 'dirty';
          dot.setAttribute('part', 'dirty');
          dot.textContent = '•';
          row.append(dot);
        }

        // One accessible name carrying BOTH lines, because a screen reader announcing only the first
        // gets exactly the alphabetical wall this component exists to avoid.
        row.setAttribute(
          'aria-label',
          `${a.name} — ${artifactQualifier(a)}${a.dirty ? ' — unsaved changes' : ''}`,
        );
        row.addEventListener('click', () => this.choose(a));
        return row;
      }),
    );
  }

  private renderTypes(): void {
    const chosen = this.getAttribute('type') ?? '';
    this.typeEl.replaceChildren(
      ...[{ v: '', t: 'Any type' }, ...artifactTypesOf(this.data).map((t) => ({ v: t, t }))].map(({ v, t }) => {
        const o = document.createElement('option');
        o.value = v;
        o.textContent = t;
        o.selected = v === chosen;
        return o;
      }),
    );
  }
}

define('caos-artifact-list', CaosArtifactList);

declare global {
  interface HTMLElementTagNameMap {
    'caos-artifact-list': CaosArtifactList;
  }
}
